// src/hooks/useAgentA2AEndpointRegistry.ts

import React from 'react';
import {
  toItems,
  useCreateAgentA2AEndpointMutation,
  useDeleteAgentA2AEndpointMutation,
  useFetchAgentA2AEndpoints,
  useUpdateAgentA2AEndpointMutation,
} from './ai-hooks';
import { ID } from '../types/aiTypes';

export function useAgentA2AEndpointRegistry() {
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editorInitialValue, setEditorInitialValue] =
    React.useState<any | null>(null);

  const {
    data: endpointsResp,
    refetch: refetchEndpoints,
    isFetching: endpointsLoading,
  } = useFetchAgentA2AEndpoints();

  const endpoints = React.useMemo<any[]>(
    () => toItems(endpointsResp),
    [endpointsResp]
  );

  const createEndpoint = useCreateAgentA2AEndpointMutation();
  const updateEndpoint = useUpdateAgentA2AEndpointMutation();
  const deleteEndpoint = useDeleteAgentA2AEndpointMutation();

  const openCreate = React.useCallback(() => {
    setEditorInitialValue(null);
    setEditorOpen(true);
  }, []);

  const openEdit = React.useCallback((row: any) => {
    setEditorInitialValue(row);
    setEditorOpen(true);
  }, []);

  const closeEditor = React.useCallback(() => {
    setEditorOpen(false);
    setEditorInitialValue(null);
  }, []);

  const saveEndpoint = React.useCallback(
    async (payload: any) => {
      if (payload?.id) {
        await updateEndpoint.mutateAsync(payload);
      } else {
        await createEndpoint.mutateAsync(payload);
      }

      closeEditor();
      await refetchEndpoints();
    },
    [closeEditor, createEndpoint, refetchEndpoints, updateEndpoint]
  );

  const removeEndpoint = React.useCallback(
    async (id: ID) => {
      if (!window.confirm('Delete this A2A endpoint?')) return;

      await deleteEndpoint.mutateAsync(id);
      await refetchEndpoints();
    },
    [deleteEndpoint, refetchEndpoints]
  );

  return {
    endpoints,
    endpointsLoading,
    refreshAll: refetchEndpoints,

    editorOpen,
    editorInitialValue,
    openCreate,
    openEdit,
    closeEditor,
    saveEndpoint,
    removeEndpoint,

    saving: createEndpoint.isPending || updateEndpoint.isPending,
  };
}
